/**
 * Defers analytics / 3rd-party tag initialization until **after the first
 * meaningful user interaction** (scroll, click, key, touch, pointer move)
 * or a generous idle-timeout fallback.
 *
 * Why: heavyweight analytics SDKs (PostHog, GA, Sentry replay, Hotjar, etc.)
 * commonly add 30–80 KB of parse/eval and run setup work that competes with
 * LCP, INP, and TBT. Loading them post-interaction keeps the initial JS
 * impact at zero while still capturing 99% of real sessions (a user who
 * never interacts also doesn't generate analytics signal worth measuring).
 *
 * Usage:
 *   // App.tsx
 *   deferAnalytics(async () => {
 *     const { init } = await import("./posthog");
 *     init();
 *   });
 *
 *   // anywhere in the app
 *   track("cta_click", { id: "hero" });
 *
 * Events emitted via `track()` before the loader resolves are buffered
 * (capped at 50 entries) and flushed in order to the registered handler.
 */

type Loader = () => void | Promise<void>;
type Handler = (event: string, props?: Record<string, unknown>) => void;

const FALLBACK_TIMEOUT_MS = 10_000;
const QUEUE_LIMIT = 50;
const TRIGGERS: Array<keyof WindowEventMap> = [
  "pointerdown",
  "click",
  "keydown",
  "scroll",
  "touchstart",
];

let scheduled = false;
let initialized = false;
const queue: Array<{ event: string; props?: Record<string, unknown> }> = [];
let handler: Handler | null = null;

function flush() {
  if (!handler) return;
  while (queue.length) {
    const e = queue.shift()!;
    try {
      handler(e.event, e.props);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] handler error", err);
    }
  }
}

/**
 * Register an analytics loader. The loader is executed exactly once,
 * on the first user interaction or after FALLBACK_TIMEOUT_MS, whichever
 * comes first. Safe to call multiple times — subsequent calls are no-ops.
 */
export function deferAnalytics(loader: Loader): void {
  if (scheduled || typeof window === "undefined") return;
  scheduled = true;

  let fired = false;
  let timeoutId: number | undefined;

  const cleanup = () => {
    for (const ev of TRIGGERS) {
      window.removeEventListener(ev, run, { capture: true } as AddEventListenerOptions);
    }
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  };

  function run() {
    if (fired) return;
    fired = true;
    cleanup();

    // Yield to the browser before doing the actual work so we don't
    // contend with the user's interaction.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const exec = async () => {
      try {
        await loader();
        initialized = true;
        flush();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[analytics] init failed", err);
      }
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(exec, { timeout: 1500 });
    } else {
      window.setTimeout(exec, 0);
    }
  }

  for (const ev of TRIGGERS) {
    // Passive + capture so we don't interfere with scroll/click handlers.
    window.addEventListener(ev, run, { passive: true, capture: true, once: false });
  }
  timeoutId = window.setTimeout(run, FALLBACK_TIMEOUT_MS);
}

/**
 * Register the function that actually delivers an event to the analytics
 * provider. Call this from inside your loader once the SDK is ready.
 */
export function setAnalyticsHandler(fn: Handler): void {
  handler = fn;
  if (initialized) flush();
}

/**
 * Track an event. Safe to call any time — events fired before the loader
 * resolves are buffered (FIFO, max 50) and replayed once the handler is
 * registered.
 */
export function track(event: string, props?: Record<string, unknown>): void {
  if (handler && initialized) {
    try {
      handler(event, props);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] handler error", err);
    }
    return;
  }
  if (queue.length >= QUEUE_LIMIT) queue.shift();
  queue.push({ event, props });
}

/** True once the loader has resolved successfully. */
export function isAnalyticsReady(): boolean {
  return initialized;
}
